import * as Sentry from '@sentry/react';
import { useIsMutating } from '@tanstack/react-query';
import { Alert, Form, Input, message, Space, Radio, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { t, jt } from 'ttag';

import Label from 'common/components/Label';
import UnstyledCollapse from 'common/components/UnstyledCollapse';

import { ERR_GATEWAY_CONN_EXISTS, GATEWAY_ENVIRONMENTS } from './constants';
import { updateConnectionMutationOptions, useUpdateConnectionMutation } from './queries';

export const EDIT_CONNECTION_FORM = 'edit-connection-form';

const EditForm = ({ initialValues = null, showEnvironmentField = false, onClose }) => {
  const tooltipRef = useRef(null);
  const [form] = Form.useForm();
  const { mutate: mutateUpdate, error: updateConnectionError } = useUpdateConnectionMutation();
  const { organizationId } = useParams();

  const isUpdating = !!useIsMutating(updateConnectionMutationOptions(organizationId));

  const handleSubmit = async values => {
    mutateUpdate(
      { id: initialValues.id, payload: { name: values.name } },
      {
        onSuccess: () => {
          onClose();
          message.success(t`Successfully updated the connection.`);
        },
        onError: err => {
          const code = err.errors?.[0].code;
          if (code === ERR_GATEWAY_CONN_EXISTS) {
            form.setFields([
              {
                name: 'name',
                errors: [
                  t`A connection with this name already exists. Please enter a different name.`,
                ],
              },
            ]);
          } else {
            Sentry.captureException(err);
          }
        },
      },
    );
  };

  const isUpdateConnectionError =
    updateConnectionError && updateConnectionError.errors?.[0]?.code !== ERR_GATEWAY_CONN_EXISTS;

  const generateCredentialsLink = (
    <a
      key="em"
      href="https://help.eventmobi.com/en/knowledge/"
      rel="nofollow noopener noreferrer"
      target="_blank"
    >
      {t`Learn how to generate credentials for your payment gateway and use test payment cards.`}
    </a>
  );

  return (
    <Form
      layout="vertical"
      form={form}
      id={EDIT_CONNECTION_FORM}
      initialValues={{
        id: initialValues?.id,
        name: initialValues?.name,
      }}
      onFinish={handleSubmit}
      requiredMark={false}
      disabled={isUpdating}
    >
      <UnstyledCollapse condition={isUpdateConnectionError}>
        <Alert
          style={{ marginBottom: '20px' }}
          showIcon
          type="error"
          title={t`Error updating connection. Please try again.`}
        />
      </UnstyledCollapse>

      <Form.Item
        name="name"
        label={<Label isRequired>{t`Name`}</Label>}
        rules={[{ required: true, message: t`Name is required` }]}
      >
        <Input />
      </Form.Item>

      {showEnvironmentField && (
        <Form.Item label={<Label>{t`Environment`}</Label>}>
          <Space orientation="vertical" size={8}>
            <Radio.Group
              value={
                initialValues.isSandbox
                  ? GATEWAY_ENVIRONMENTS.Sandbox
                  : GATEWAY_ENVIRONMENTS.Production
              }
            >
              <Radio.Button value={GATEWAY_ENVIRONMENTS.Production} disabled>
                {t`Production`}
              </Radio.Button>
              <Radio.Button value={GATEWAY_ENVIRONMENTS.Sandbox} disabled>
                {t`Sandbox`}
              </Radio.Button>
            </Radio.Group>

            <Typography.Text type="secondary">
              {initialValues.isSandbox &&
                jt`This is a safe testing environment that allows you to simulate payment transactions without using real money or credit cards. ${generateCredentialsLink}}`}
              {!initialValues.isSandbox &&
                t`This is a live environment that allows you to process transactions using real money and real customer information. The environment cannot be changed once the connection is established.`}
            </Typography.Text>
            <Alert
              showIcon
              type="info"
              title={t`If you would like to switch environments, a separate gateway setup is required.`}
              style={{ marginTop: 8 }}
            />
          </Space>
        </Form.Item>
      )}

      <div ref={tooltipRef} />
    </Form>
  );
};

EditForm.propTypes = {
  initialValues: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  onClose: PropTypes.func.isRequired,
  showEnvironmentField: PropTypes.bool,
};

export default EditForm;
